import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Col, Row } from 'components/Core/Grid';
import FormLabelWithField from 'components/Core/FormLabelWithField';
import Button from 'components/Core/Button';
import NumberInputField from 'components/Core/NumberInputField';
import { getFormValid } from 'utils/formUtils';
import EmbedForm from '../../EmbeddableForm';
import EmbedLink from '../../EmbedLink';

const RoofingMaterialsCalcForm = ({ pathUrl, onSubmit, show, ...otherProps }) => {
  const onCalc = ({ roofArea }) => {
    const area = parseFloat(roofArea);
    const bundlesOfShingles = Math.ceil(area / 33.3);
    const rollsOfRollRoofing = Math.ceil(area / 100);
    const rollsOf15Felt = Math.ceil(area / 400);
    const rollsOf30Felt = Math.ceil(area / 200);

    onSubmit({
      bundlesOfShingles,
      rollsOfRollRoofing,
      rollsOf15Felt,
      rollsOf30Felt,
    });
  };

  const formik = useFormik({
    initialValues: {
      roofArea: 2000,
    },
    validationSchema: Yup.object().shape({
      roofArea: Yup.number().required('Roof Area is required'),
    }),
    onSubmit: (_values) => {
      onCalc(_values);
    },
  });

  const onHandleChange = (id, newValue) => {
    formik.setFieldTouched(id);
    formik.setFieldValue(id, newValue);
  };

  const formValid = getFormValid({ ...formik });

  return (
    <div className={`w-full h-full absolute top-0 ${show ? 'z-10' : 'z-0'}`}>
      <div id="roofing_materials_calc_form">
        <div
          className={`transition-all duration-500 ${
            show ? 'visible opacity-100' : 'invisible opacity-0'
          }`}
          data-position="roofing angle form"
        >
          <div className="mb-[20px]">
            <FormLabelWithField label="Roof Area (Square Feet)">
              <NumberInputField
                id="roofArea"
                min={0}
                max={999999999999}
                values={formik.values}
                formValid={formValid}
                handleChange={onHandleChange}
              />
            </FormLabelWithField>
          </div>
        </div>
      </div>
      <div className={`absolute bottom-0 w-full ${show ? 'visible' : 'invisible'}`}>
        <Button
          className="!w-full"
          type="Purple"
          shadow={false}
          onClick={() => formik.submitForm()}
        >
          Calculate
        </Button>
        <div className="form-embed-link">
          <EmbedLink
            show={!otherProps?.isEmbed}
            openCopyEmbedLinkDialog={otherProps?.openCopyEmbedLinkDialog}
            label="Add tool to your website"
            pathUrl={pathUrl}
          />
        </div>
      </div>
    </div>
  );
};

export default EmbedForm(RoofingMaterialsCalcForm);

export const RoofingMaterialsCalcResult = ({ result }) => (
  <div className="flex flex-col items-center self-stretch gap-[20px] md:gap-[40px]">
    <h4 className="text-[16px] font-black text-[var(--grey-8)] leading-[110%] tracking-[0.8px] uppercase mb-0">
      Estimated Results
    </h4>
    <Row className="w-full" gutter={[20, 40]}>
      <Col lg={12} md={24} sm={24} xs={24} className="flex flex-col items-center">
        <p className="text-[16px] text-black text-center font-normal leading-[150%] mb-0">
          Bundles of composition shingles:
        </p>
        <h3 className="text-[28px] text-black font-extrabold leading-[120%] tracking-[-1.12px] mb-0">
          {result.bundlesOfShingles}
          &nbsp;bundles
        </h3>
        <p className="text-[12px] text-black font-normal leading-[120%] mb-0">
          (each bundle will cover ~33 ft2)
        </p>
      </Col>
      <Col lg={12} md={24} sm={24} xs={24} className="flex flex-col items-center">
        <p className="text-[16px] text-black text-center font-normal leading-[150%] mb-0">
          Rolls of roll roofing:
        </p>
        <h3 className="text-[28px] text-black font-extrabold leading-[120%] tracking-[-1.12px] mb-0">
          {result.rollsOfRollRoofing}
          &nbsp;rolls
        </h3>
        <p className="text-[12px] text-black font-normal leading-[120%] mb-0">
          (~36 in × 36 ft for each roll)
        </p>
      </Col>
      <Col lg={12} md={24} sm={24} xs={24} className="flex flex-col items-center">
        <p className="text-[16px] text-black text-center font-normal leading-[150%] mb-0">
          Rolls of #15 felt:
        </p>
        <h3 className="text-[28px] text-black font-extrabold leading-[120%] tracking-[-1.12px] mb-0">
          {result.rollsOf15Felt}
          &nbsp;rolls
        </h3>
        <p className="text-[12px] text-black font-normal leading-[120%] mb-0">
          (~36 in × 144 ft for each roll)
        </p>
      </Col>
      <Col lg={12} md={24} sm={24} xs={24} className="flex flex-col items-center">
        <p className="text-[16px] text-black text-center font-normal leading-[150%] mb-0">
          Rolls of #30 felt:
        </p>
        <h3 className="text-[28px] text-black font-extrabold leading-[120%] tracking-[-1.12px] mb-0">
          {result.rollsOf30Felt}
          &nbsp;rolls
        </h3>
        <p className="text-[12px] text-black font-normal leading-[120%] mb-0">
          (~36 in × 72 ft for each roll)
        </p>
      </Col>
    </Row>
  </div>
);
